import { Box, Button, HStack, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

interface ReadyToLeaveNotificationProps {
  onLeave: () => void;
}

const COUNTDOWN_SECONDS = 120; // 2 minutes
const COUNTDOWN_MS = COUNTDOWN_SECONDS * 1000;

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const ReadyToLeaveNotification: React.FC<
  ReadyToLeaveNotificationProps
> = ({ onLeave }) => {
  const [secondsRemaining, setSecondsRemaining] = useState(COUNTDOWN_SECONDS);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    // Capture start time when component mounts
    startTimeRef.current = Date.now();

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, COUNTDOWN_MS - elapsed);
      const secondsLeft = Math.ceil(remaining / 1000);

      setSecondsRemaining(secondsLeft);

      // Clear interval when countdown reaches 0
      if (remaining <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Call onLeave when timer reaches 0 (in a separate effect)
  useEffect(() => {
    if (secondsRemaining === 0) {
      onLeave();
    }
  }, [secondsRemaining, onLeave]);

  return (
    <Box
      position="fixed"
      top="20px"
      left="50%"
      transform="translateX(-50%)"
      zIndex={9999}
      bg="rgba(0, 0, 0, 0.85)"
      color="white"
      borderRadius="8px"
      px={6}
      py={4}
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
      maxW="500px"
    >
      <HStack spacing={4} justify="space-between" align="center">
        <HStack spacing={3}>
          <Text fontSize="md" fontWeight="medium">
            You&apos;re the only participant in this call.
          </Text>
          <Text fontSize="md" fontWeight="bold" color="orange.300">
            {formatTime(secondsRemaining)}
          </Text>
        </HStack>
        <Button
          size="sm"
          colorScheme="red"
          onClick={onLeave}
          fontWeight="semibold"
        >
          Exit
        </Button>
      </HStack>
    </Box>
  );
};
